import { Button } from "antd";
import { Loader, LucideUpload, Search, SortAsc } from "lucide-react";
import { useMutation, useQuery } from "react-query";
import {
  downloadAuditTrail,
  downloadSelectedAuditTrail,
  getAuditTrail,
} from "../../sdk/trail/trail";
import { useState } from "react";
import { useError } from "../../context/ErrorProvider";
import Pagination from "../../shared/Pagination";
import Trail from "../../components/audit-trail/Trail";
import AuditFilter from "../../components/filter/AuditFilter";
import Loading from "../../shared/Loading";
import { SkeletonDashboard } from "../../skeletons/DashboardSkeleton";
import { downloadExportFile } from "../../utils/export";

const SystemLogs = () => {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(25);
  const [fromDate, setFromDate] = useState(new Date("2023-11-23"));
  const [toDate, setToDate] = useState(new Date());
  const [auditTrails, setAuditTrails] = useState([]);
  const { showError } = useError();
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [openAuditTrail, setOpenAuditTrail] = useState(false);
  const [openAuditTrailFilter, setOpenAuditTrailFilter] = useState(false);
  const [selectedTrail, setSelectedTrail] = useState({});

  // Toggle a single checkbox
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  // Toggle all checkboxes
  const toggleSelectAll = () => {
    if (selectedIds.length === auditTrails.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(auditTrails.map((log) => log.id));
    }
  };

  const toggleAuditTrail = (log) => {
    setSelectedTrail(log);
    setOpenAuditTrail(true);
  };
  const closeAuditTrail = () => {
    setOpenAuditTrail(false);
  };

  const toggleAuditTrailFilter = () => {
    setOpenAuditTrailFilter(true);
  };
  const closeAuditTrailFilter = () => {
    setOpenAuditTrailFilter(false);
  };

  const handlePageChange = (page) => {
    setPage(page);
  };

  const {
    refetch: refetchTrails,
    isRefetching,
    isLoading,
  } = useQuery({
    queryKey: [
      "get audit trails",
      page,
      limit,
      fromDate ? fromDate.toISOString() : null,
      toDate ? toDate.toISOString() : null,
    ],
    queryFn: () =>
      getAuditTrail(
        page,
        limit,
        fromDate ? fromDate.toISOString() : null,
        toDate ? toDate.toISOString() : null,
        search,
      ),
    onSuccess: (data) => {
      setAuditTrails(data.rows);
      setPage(data.page);
      setLimit(data.limit);
      setTotalPages(data.totalPages);
      setTotalItems(data?.total);
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const { mutate: downloadTrails, isLoading: loadingDownloadTrails } =
    useMutation({
      mutationKey: [
        "download audit trails",
        page,
        limit,
        fromDate ? fromDate.toISOString() : null,
        toDate ? toDate.toISOString() : null,
        search,
      ],
      mutationFn: () =>
        downloadAuditTrail(
          page,
          limit,
          fromDate ? fromDate.toISOString() : null,
          toDate ? toDate.toISOString() : null,
          search,
          "csv",
        ),
      onSuccess: (blob) => {
        downloadExportFile(blob, `audit-trail.csv`);
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const { mutate: downloadSelected, isLoading: loadingSelectedDownloadTrails } =
    useMutation({
      mutationKey: ["Download selected Trails"],
      mutationFn: () => downloadSelectedAuditTrail(selectedIds, "csv"),
      onSuccess: (blob) => {
        downloadExportFile(blob, `AuditTrail.csv`);
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  function deriveActionDescription(method, route) {
    // Clean the route and extract meaningful parts
    const cleanRoute = route.replace(/^\/api\/v\d+\//, "").replace(/-/g, " ");
    const parts = cleanRoute.split("/").filter(Boolean); // Split into parts

    // Join meaningful parts into a phrase
    const resource = parts.join(" ");

    // Map HTTP method to an action verb
    const methodActionMap = {
      GET: "View",
      POST: "Create",
      PUT: "Update",
      PATCH: "Modify",
      DELETE: "Delete",
    };

    const actionVerb = methodActionMap[method.toUpperCase()] || method;

    // Return the final description
    return `${actionVerb} ${resource}`;
  }

  function deriveModuleFromRoute(route) {
    // Remove '/api/' prefix and optional version like 'v1/', 'v2/', etc.
    const cleanRoute = route.replace(/^\/api\/(v\d+\/)?/, "");

    // Split by '/' and take the first part
    const [firstSegment] = cleanRoute.split("/").filter(Boolean);

    // Replace dashes with spaces and return as module name
    return firstSegment ? firstSegment.replace(/-/g, " ") : "unknown";
  }

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalItems);

  return (
    <>
      {openAuditTrail && (
        <Trail closeAuditTrail={closeAuditTrail} auditData={selectedTrail} />
      )}
      {openAuditTrailFilter && (
        <AuditFilter
          closeFilters={closeAuditTrailFilter}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          handleApplyFilter={closeAuditTrailFilter}
        />
      )}
      {isLoading ? (
        <SkeletonDashboard />
      ) : (
        <div className="w-full mb-[10px] pl-[10px]">
          <p className="font-bold text-[24px] text-[#222222]">Audit Trails</p>
          <div className="w-full p-[10px]">
            <div className="h-[70px] w-full bg-white flex items-center justify-between px-[10px]">
              <button
                onClick={refetchTrails}
                className="h-[35px] w-[120px] flex items-center justify-center gap-[5px] border border-[#00A676] bg-white rounded-[10px] text-[#00A676] font-medium text-[15px]"
              >
                {isRefetching ? <Loading /> : <Loader size={17} />}
                Update
              </button>
              <div className="h-[40px] w-[117px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-white flex items-center justify-center">
                {selectedIds?.length} Selected
              </div>
              <div
                onClick={toggleAuditTrailFilter}
                className="h-[40px] w-[127px] cursor-pointer text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-[#FBF5F5] flex items-center justify-center gap-[5px]"
              >
                <SortAsc color="#000000" />
                <span className="text-[#000000]">Filter</span>
              </div>
              <div className="flex items-center border border-gray-300 rounded-md px-2 h-[40px] w-[50%] bg-white">
                <Search className="text-gray-500 w-5 h-5 mr-2" />
                <input
                  type="text"
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search logs, users..."
                  className="w-full outline-none text-sm text-gray-700 placeholder-gray-400 bg-transparent"
                />
                <button
                  onClick={refetchTrails}
                  className="ml-2 p-2 bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  <Search className="w-4 h-4 text-white" />
                </button>
              </div>
              <p className="text-[#000000] truncate text-[15px] font-normal">
                {auditTrails?.length} Results
              </p>
              {selectedIds?.length > 0 ? (
                <Button
                  disabled={loadingSelectedDownloadTrails}
                  onClick={downloadSelected}
                  className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]"
                >
                  {loadingSelectedDownloadTrails ? (
                    <Loading />
                  ) : (
                    <LucideUpload size={17} />
                  )}
                  <span>Export</span>
                </Button>
              ) : (
                <Button
                  disabled={loadingDownloadTrails}
                  onClick={downloadTrails}
                  className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]"
                >
                  {loadingDownloadTrails ? (
                    <Loading />
                  ) : (
                    <LucideUpload size={17} />
                  )}
                  <span>Export</span>
                </Button>
              )}
            </div>
            <div className="w-full h-[40px] mb-[7px] text-[13px] px-[10px] bg-[#F3F3F3] flex items-center">
              <div className="w-[3%] px-[10px] text-[#000000] font-bold">
                <input
                  type="checkbox"
                  checked={selectedIds.length === auditTrails.length}
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="w-[7%] px-[10px] text-[#000000] font-bold">
                ID
              </div>
              <div className="w-[10%] truncate px-[10px] text-[#000000] font-bold">
                ACTION CATEGORY
              </div>
              <div className="w-[17%] truncate px-[10px] text-[#000000] font-bold">
                ACTION DESCRIPTION
              </div>
              <div className="w-[11.4%] px-[10px] text-[#000000] font-bold">
                ENTITY AFFECTED
              </div>
              <div className="w-[11.5%] px-[10px] text-[#000000] font-bold">
                USER
              </div>
              <div className="w-[25%] px-[10px] text-[#000000] font-bold">
                USER DEVICE
              </div>
              <div className="w-[15%] px-[10px] text-[#000000] font-bold">
                DATE/TIME
              </div>
            </div>
            {auditTrails?.length > 0 ? (
              auditTrails?.map((log) => (
                <div className="w-full h-[37px] text-[13px] px-[10px] mb-[7px] bg-[#ffffff] flex items-center">
                  <div className="w-[3%] px-[10px] truncate text-[#000000] font-normal">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(log.id)}
                      onChange={() => toggleSelect(log.id)}
                    />
                  </div>
                  <div
                    onClick={() => toggleAuditTrail(log)}
                    className="w-[7%] px-[10px] truncate text-[#2A8C52] cursor-pointer font-normal"
                  >
                    {log?.id?.slice(0, 8)}
                  </div>
                  <div className="w-[10%] px-[10px] truncate capitalize text-[#000000] font-normal">
                    {deriveModuleFromRoute(log?.route)}
                  </div>
                  <div className="w-[17%] px-[10px] truncate text-[#000000] font-normal">
                    {deriveActionDescription(log?.method, log?.route)}
                  </div>
                  <div className="w-[11.5%] px-[10px] truncate text-[#000000] font-normal">
                    {log?.route}
                  </div>
                  <div className="w-[11.5%] px-[10px] truncate text-[#000000] font-normal">
                    {log?.userId ?? "N/A"}
                  </div>
                  <div className="w-[25%] px-[10px] truncate text-[#000000] font-normal">
                    {`${log?.userAgent} - ${log?.ip}`}
                  </div>
                  <div className="w-[15%] px-[10px] truncate text-[#000000] font-normal">
                    {
                      new Date(log?.createdAt)
                        .toISOString()
                        .replace("T", " ")
                        .split(".")[0]
                    }
                  </div>
                </div>
              ))
            ) : (
              <div className="w-full py-6 text-gray-500">
                No audit trails available.
              </div>
            )}
            {auditTrails?.length > 0 && (
              <div className="h-[50px] mb-[20px] flex items-center justify-between">
                <div className="flex items-center gap-[10px]">
                  <p className="text-[15px] text-[#5C5C5C] font-normal">
                    {start}–{end} of {totalItems}
                  </p>
                  <p className="text-[15px] text-[#5C5C5C] font-normal">
                    Row/Page:
                  </p>
                  <p className="text-[15px] text-[#5C5C5C] font-normal">
                    {page}/{totalPages}
                  </p>
                  <select
                    className="h-[35px] w-[100px] px-[10px] text-[12px]"
                    name="row"
                    id="row"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                  >
                    <option value="10">10 items</option>
                    <option value="20">20 items</option>
                    <option value="25">25 items</option>
                  </select>
                </div>
                <div>
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SystemLogs;
